import { useEffect, useState } from 'preact/hooks';
import { PO_CSS, renderPoDocument, type PoDocData } from '../../shared/po/document';
import { useI18n } from '../i18n';
import { ApiError, apiGet, apiSend } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/dates';

// The PO document (screens 11–13, P3.4): the same bilingual renderer the broker converts to PDF,
// shown on screen and printed with the browser (the fallback named in CLAUDE.md, and how anyone
// keeps a copy). Procurement can file it again and email it to the supplier (D55).

interface DocBody {
  data: PoDocData;
  pdf: string;
  canSend: boolean;
  supplierEmail: string;
  /** When it was last emailed to the supplier; '' when not yet (D93). Procurement only. */
  sentAt: string;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; body: DocBody };

let adopted = false;
/** The document CSS as a constructed stylesheet: CSSOM is not inline style, so the CSP holds. */
function adoptStyles(): void {
  if (adopted || typeof CSSStyleSheet === 'undefined' || !('adoptedStyleSheets' in document)) {
    return;
  }
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(PO_CSS);
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
  adopted = true;
}

export function PoDocumentPage({ poNo }: { poNo: string }) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [sentNow, setSentNow] = useState('');

  useEffect(() => {
    adoptStyles();
    let live = true;
    apiGet<DocBody>(`/api/pos/${encodeURIComponent(poNo)}/document`)
      .then((body) => live && setLoad({ kind: 'ok', body }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [poNo]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>{load.code === 'forbidden' ? t('pod.error.forbidden') : t('req.loadError')}</p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
      </section>
    );
  }
  const { body } = load;

  async function send() {
    setBusy(true);
    setNote(null);
    try {
      const out = await apiSend<{ pdf: boolean; emailed: boolean }>(
        'POST',
        `/api/pos/${encodeURIComponent(poNo)}/send`,
        {},
      );
      if (out.emailed) setSentNow(new Date().toISOString());
      setNote(
        out.emailed
          ? { ok: true, text: t('pod.sent', { email: body.supplierEmail }) }
          : { ok: false, text: out.pdf ? t('pod.notSent') : t('pod.noPdf') },
      );
    } catch {
      setNote({ ok: false, text: t('pod.notSent') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="stack">
      <div class="pod-toolbar no-print">
        <p class="caption">
          <a href={`#/requests/${body.data.prNo}`} dir="ltr">
            {body.data.prNo}
          </a>{' '}
          / <span dir="ltr">{poNo}</span>
        </p>
        <div class="actions">
          <button type="button" class="button" onClick={() => window.print()}>
            {t('pod.print')}
          </button>
          {body.canSend && (
            <button
              type="button"
              class="button button--primary"
              disabled={busy}
              onClick={() => void send()}
            >
              {t('pod.send')}
            </button>
          )}
        </div>
        <p class="caption">
          {body.pdf ? t('pod.filed', { name: body.pdf }) : t('pod.notFiled')}
          {body.data.approval.state === 'pending' && ` · ${t('pod.pending')}`}
          {body.data.terms.draft && ` · ${t('pod.draftTerms')}`}
          {body.canSend && body.supplierEmail && ` · ${t('pod.to', { email: body.supplierEmail })}`}
          {body.data.poDate && ` · ${formatDate(body.data.poDate, 'en')}`}
        </p>
        {body.canSend &&
          body.data.approval.state !== 'pending' &&
          (sentNow || body.sentAt ? (
            <p class="notice notice--info">
              {t('pod.sentOn', { date: formatDateTime(sentNow || body.sentAt, lang) })}
            </p>
          ) : (
            <p class="notice notice--action">
              <strong>{t('pod.notSentYet')}</strong> {t('pod.notSentYetHint')}
            </p>
          ))}
        {note && (
          <p class={note.ok ? 'form-saved' : 'form-problem'} role={note.ok ? 'status' : 'alert'}>
            {note.text}
          </p>
        )}
      </div>
      {/* Our own renderer's output: every value in it is escaped (shared/po/document.ts). */}
      <div
        class="pod-sheet"
        dir="ltr"
        dangerouslySetInnerHTML={{ __html: renderPoDocument(body.data) }}
      />
    </div>
  );
}

interface VerifyBody {
  docNo: string;
  kind: string;
  entity: string;
  issuedAt: string;
  status: string;
  signed: boolean;
}

/** The QR target: status only (08-DOCUMENTS). */
export function VerifyPage({ docNo }: { docNo: string }) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ok'; body: VerifyBody }
  >({ kind: 'loading' });
  useEffect(() => {
    let live = true;
    apiGet<VerifyBody>(`/api/verify/${encodeURIComponent(docNo)}`)
      .then((body) => live && setLoad({ kind: 'ok', body }))
      .catch((err: unknown) => {
        if (live) setLoad({ kind: 'error', code: err instanceof ApiError ? err.code : 'generic' });
      });
    return () => {
      live = false;
    };
  }, [docNo]);
  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>{load.code === 'not_found' ? t('verify.notFound') : t('req.loadError')}</p>
      </section>
    );
  }
  const b = load.body;
  return (
    <section class="card" aria-labelledby="verify-title">
      <h2 id="verify-title" class="card__title" dir="ltr">
        {b.docNo}
      </h2>
      <p>
        <span class={`pill pill--${b.signed ? 'success' : 'warning'}`}>
          {b.kind === 'DN' ? t('verify.dn') : b.signed ? t('verify.issued') : t('verify.pending')}
        </span>
      </p>
      <p class="caption">
        {t('verify.facts', { entity: b.entity, date: formatDate(b.issuedAt, lang) })}
      </p>
    </section>
  );
}
